import { useState } from "react";

import {
  Switch,
  TableCellBadge,
  TableCellText,
  TableHeadCellCustom,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type StatusVariants,
  type SubMenuConfig,
} from "@/components/molecules";
import {
  brandListMocks,
  countryListMock,
  systemFeaturedBrandTableMock,
  type SystemFeaturedBrandTableMockType,
} from "@/modules/mocksData";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";

import DynamicTable from "../DynamicTable";
import type { ColumnConfig } from "../type";

enum SystemFeaturedBrandColumn {
  BRAND = "Brand",
  COUNTRY = "Country",
  VISIBILITY = "Visibility",
  FEATURED = "Featured",
}

const TABLE_HEADERS: TableHeaderType = Object.values(SystemFeaturedBrandColumn);

const SUB_MENUS = {
  [SystemFeaturedBrandColumn.BRAND]: brandListMocks,
  [SystemFeaturedBrandColumn.COUNTRY]: countryListMock,
  Status: ["Active", "Inactive"],
};

const MENU_LIST = Object.keys(SUB_MENUS);

const STATUS_VARIANT_MAP: StatusVariants = {
  Active: { badge: "success", dot: "#65A30D" },
};

const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
  [SystemFeaturedBrandColumn.BRAND]: {
    type: "search",
    placeholder: "Search brand",
  },
  [SystemFeaturedBrandColumn.COUNTRY]: { type: "role" },
  Status: { type: "status" },
};

const COLUMN_WIDTHS = "w-[25%]";

const SystemFeaturedBrandsTable = ({
  hasUpdatePermission,
}: {
  hasUpdatePermission: boolean;
}) => {
  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);
  const [searchValue, setSearchValue] = useState("");

  const [brandStates, setBrandStates] = useState(() =>
    systemFeaturedBrandTableMock.map(item => ({
      id: item.id,
      featured: item.featured,
    }))
  );

  const toggleFeatured = (id: string) => {
    setBrandStates(prev =>
      prev.map(item =>
        item.id === id ? { ...item, featured: !item.featured } : item
      )
    );
  };

  const columns: ColumnConfig<SystemFeaturedBrandTableMockType>[] = [
    {
      key: "brand",
      label: SystemFeaturedBrandColumn.BRAND,
      visible: flags[SystemFeaturedBrandColumn.BRAND],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellBadge classes={COLUMN_WIDTHS} last={isLast} stage="outline">
          {row.brand}
        </TableCellBadge>
      ),
    },
    {
      key: "country",
      label: SystemFeaturedBrandColumn.COUNTRY,
      visible: flags[SystemFeaturedBrandColumn.COUNTRY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText classes={COLUMN_WIDTHS} last={isLast}>
          {row.country}
        </TableCellText>
      ),
    },
    {
      key: "status",
      label: SystemFeaturedBrandColumn.VISIBILITY,
      visible: flags[SystemFeaturedBrandColumn.VISIBILITY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellBadge
          classes={COLUMN_WIDTHS}
          last={isLast}
          stage={row.status.stage}
        >
          {row.status.title}
        </TableCellBadge>
      ),
    },
    {
      key: "featured",
      label: SystemFeaturedBrandColumn.FEATURED,
      visible: flags[SystemFeaturedBrandColumn.FEATURED],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        const featuredState =
          brandStates.find(item => item.id === row.id)?.featured ?? false;
        return (
          <TableCellText classes={COLUMN_WIDTHS} last={isLast}>
            <Switch
              id="featured"
              checked={featuredState}
              onCheckedChange={() => toggleFeatured(row.id)}
              data-testid={`system-feature-switch-${row.id}`}
              disabled={!hasUpdatePermission}
            />
          </TableCellText>
        );
      },
    },
  ];

  // TODO: add props during integration
  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        statusVariant={STATUS_VARIANT_MAP}
        classes="mt-5"
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        // handleSetFilter={handleSetFilter}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      </FilterSearch>
      <DynamicTable
        data={systemFeaturedBrandTableMock}
        columns={columns}
        // searchValue={searchValue}
        // next={handleNext}
        // prev={handlePrev}
        // tableParams={data}
        // setTableParams={setTableParams}
      />
    </>
  );
};

export default SystemFeaturedBrandsTable;
